import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc as docRef,
  getDoc,
  Timestamp,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { db } from "../../Firebase/firebaseconfig";
import notify from "../common/notify";

import * as agendaCobranza from "../../helpers/agendaCobranza";
import {
  calcularPerfilCobranzaV2,
  getFechaInicioReal,
} from "../../helpers/cobranzaInteligenteV2";
import {
  listarPromesasGlobal,
  listarGestionesGlobal,
} from "../../services/cobranzaOperativaService";
import {
  listarSnapshotsRecientes,
} from "../../services/cobranzaSnapshotsService";
import { toJsDate, toJsMs } from "../../helpers/fechas";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import "./AgendaPrevisoria.css";

const AgendaPrevisoria = () => {
  const navigate = useNavigate();

  const clientesCache = useRef(new Map());
  const fraccionamientosCache = useRef(new Map());

  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
 

  const [filtroTramo, setFiltroTramo] = useState("todos");
  const [busqueda, setBusqueda] = useState("");
  const [fraccionamiento, setFraccionamiento] = useState("todos");
  const [orden, setOrden] = useState("vencimiento");
  const [filtroCobrador, setFiltroCobrador] = useState("todos");
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [filtroDia, setFiltroDia] = useState(null); // dueDay numérico al hacer click en barra
  const [vistaCompacta, setVistaCompacta] = useState(true);
  const [agrupar, setAgrupar] = useState(false);
  const [seleccionados, setSeleccionados] = useState(() => new Set());

  // Datos auxiliares para enriquecimiento
  const [promesasPendientes, setPromesasPendientes] = useState([]);
  const [gestionesRecientes, setGestionesRecientes] = useState([]);
  const [snapshots, setSnapshots] = useState([]);

  // Carga inicial de promesas, gestiones, snapshots
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [promesas, gestiones, snaps] = await Promise.all([
          listarPromesasGlobal({ estado: "pendiente" }).catch(() => []),
          listarGestionesGlobal({}).catch(() => []),
          listarSnapshotsRecientes(12).catch(() => []),
        ]);
        if (!alive) return;
        setPromesasPendientes(promesas);
        const hace7dMs = Date.now() - 7 * 86400000;
        setGestionesRecientes(
          gestiones.filter((g) => (g?.creadoEn?.toMillis?.() || toJsMs(g?.creadoEn)) >= hace7dMs),
        );
        setSnapshots(snaps);
      } catch (err) {
        console.error("AgendaPrevisoria: error auxiliares", err);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const getCliente = useCallback(async (clienteId) => {
    if (!clienteId) return {};

    if (clientesCache.current.has(clienteId)) {
      return clientesCache.current.get(clienteId);
    }

    const snap = await getDoc(docRef(db, "clientes", clienteId));
    const data = snap.exists() ? snap.data() : {};
    clientesCache.current.set(clienteId, data);
    return data;
  }, []);

  const getFraccionamientoNombre = useCallback(async (dataDeuda) => {
    const fraccId = agendaCobranza.getFraccionamientoIdFromDeuda(dataDeuda);
    if (!fraccId) return agendaCobranza.getFraccionamientoNombreFallback(dataDeuda);

    if (fraccionamientosCache.current.has(fraccId)) {
      return fraccionamientosCache.current.get(fraccId);
    }

    const snap = await getDoc(docRef(db, "fraccionamientos", fraccId));
    const nombre = snap.exists()
      ? (snap.data()?.nombre || "—")
      : agendaCobranza.getFraccionamientoNombreFallback(dataDeuda);

    fraccionamientosCache.current.set(fraccId, nombre);
    return nombre;
  }, []);

  const enriquecer = useCallback(
  async (docSnap) => {
    const data = docSnap.data();

    const perfilCobranza = calcularPerfilCobranzaV2(data);

    if (!perfilCobranza?.esCobrable) {
      return null;
    }

    const cliente = await getCliente(data?.clienteId);

    let responsableNombre = "";
    if (data?.fueraPaisRegularmente && data?.responsableId) {
      const responsable = await getCliente(data.responsableId);
      responsableNombre = agendaCobranza.getNombreCompleto(responsable);
      if (responsableNombre === "Sin nombre") responsableNombre = "";
    }

    const nextDueDate = agendaCobranza.getDueTimestamp(data);
    const dueDays = agendaCobranza.getDiasHastaVencimiento(nextDueDate);

    const saldoRestante = Number(perfilCobranza.saldoRestante || 0);
    const mensualidadBase = Number(perfilCobranza.mensualidad || 0);

    const mensualidad =
      saldoRestante > 0
        ? Math.min(mensualidadBase, saldoRestante)
        : mensualidadBase;

    const saldoProximo =
      mensualidad > 0 && saldoRestante > 0
        ? Math.min(mensualidad, saldoRestante)
        : 0;

    const fechaVencimiento = toJsDate(nextDueDate);
    const diaDelMes = fechaVencimiento ? fechaVencimiento.getDate() : null;

    const fraccionamientoNombre = await getFraccionamientoNombre(data);

    // Último cobrador (del último pago positivo)
    const pagosArr = Array.isArray(data?.pagos) ? data.pagos : [];
    const pagosPositivos = pagosArr.filter((p) => Number(p?.cantidad || 0) > 0);
    const ultimoPago = pagosPositivos[pagosPositivos.length - 1] || null;
    const ultimoCobrador =
      ultimoPago?.cobradorNombre ||
      ultimoPago?.cobradorEmail ||
      "Sin cobrador";

    return {
      id: docSnap.id,
      clienteId: data?.clienteId || "",
      cliente: agendaCobranza.getNombreCompleto(cliente),
      responsableNombre,
      telefono: cliente?.telefono || "—",
      direccion: cliente?.direccion || "—",
      referencia: data?.referencia || "—",
      fraccionamiento: fraccionamientoNombre || "—",

      saldoRestante,
      mensualidad,
      saldoProximo,

      saldoAtrasado: Number(perfilCobranza.saldoAtrasado || 0),
      mesesAtraso: Number(perfilCobranza.mesesAtraso || 0),
      mesesTranscurridos: Number(perfilCobranza.mesesTranscurridos || 0),
      fechaInicioReal: getFechaInicioReal(data),
      ultimoCobrador,

      nextDueDate,
      fechaVencimiento,
      dueDays,
      diaDelMes,
      tramo: agendaCobranza.getRangoPrevision(dueDays),
      estado: data?.estado || data?.estatus || "—",
      perfilCobranza,
      raw: data,
    };
  },
  [getCliente, getFraccionamientoNombre]
);

  useEffect(() => {
    const hoy = new Date();
const inicio = Timestamp.fromDate(
  agendaCobranza.startOfDay(hoy)
);
const fin = Timestamp.fromDate(
  agendaCobranza.endOfDay(agendaCobranza.addDays(hoy, 30))
);

    const q = query(
      collection(db, "deudas"),
      where("activo", "==", true),
      where("nextDueDate", ">=", inicio),
      where("nextDueDate", "<=", fin)
    );

    const unsub = onSnapshot(
      q,
      async (snap) => {
        try {
          setLoading(true);

          const docs = await Promise.all(snap.docs.map(enriquecer));

          const validos = docs.filter(
  (item) =>
    item &&
    item.nextDueDate?.toDate &&
    item.saldoRestante > 0 &&
    item.dueDays != null &&
    item.dueDays >= 1 &&
    item.dueDays <= 30
);

          setRegistros(validos);
        } catch (error) {
          console.error("Error cargando agenda previsoria:", error);
          setRegistros([]);
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error("Error en agenda previsoria:", error);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [enriquecer]);

  
  const handleVerDetalle = useCallback(
    (deuda) => {
      navigate(`/app/cliente/${deuda.clienteId}/deudas/${deuda.id}`);
    },
    [navigate]
  );

  // === Helpers de contacto ===
  const limpiarTelefono = (tel) => String(tel || "").replace(/\D/g, "");
  const copiarTelefono = async (tel) => {
    try {
      await navigator.clipboard.writeText(tel);
      notify.success("Teléfono copiado");
    } catch {
      notify.error("No se pudo copiar");
    }
  };
  const abrirWhatsAppRecordatorio = (item) => {
    const num = limpiarTelefono(item.telefono);
    if (!num) {
      notify.error("Sin teléfono válido");
      return;
    }
    const numConLada = num.startsWith("52") ? num : `52${num}`;
    const dia = item.diaDelMes ? `el día ${String(item.diaDelMes).padStart(2, "0")}` : "próximamente";
    const monto = agendaCobranza.formatMoney(item.mensualidad || item.saldoProximo || 0);
    const mensaje = encodeURIComponent(
      `Buen día ${item.cliente || ""}, le contacto del área de cobranza para recordarle amablemente que su mensualidad de ${monto} vence ${dia}. ¿Podríamos coordinar el pago?`,
    );
    window.open(`https://wa.me/${numConLada}?text=${mensaje}`, "_blank");
  };

  // === Selección múltiple ===
  const toggleSeleccionado = (id) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const togglearTodos = (visibles) => {
    setSeleccionados((prev) => {
      const all = visibles.every((it) => prev.has(it.id));
      if (all) {
        const next = new Set(prev);
        for (const it of visibles) next.delete(it.id);
        return next;
      }
      const next = new Set(prev);
      for (const it of visibles) next.add(it.id);
      return next;
    });
  };
  const limpiarSeleccion = () => setSeleccionados(new Set());
  const enviarRecordatoriosLote = (items) => {
    if (!items.length) {
      notify.error("Sin selección");
      return;
    }
    notify.info(`Abriendo ${items.length} ventanas de WhatsApp...`);
    items.forEach((it, i) => {
      setTimeout(() => abrirWhatsAppRecordatorio(it), i * 300);
    });
  };

  // === Filtros activos para chips ===
  const limpiarFiltros = () => {
    setBusqueda("");
    setFiltroTramo("todos");
    setFraccionamiento("todos");
    setFiltroCobrador("todos");
    setFiltroEstado("todos");
    setFiltroDia(null);
  };
  const filtrosActivos = useMemo(() => {
    const chips = [];
    if (busqueda.trim())
      chips.push({ key: "busq", label: `"${busqueda.trim()}"`, clear: () => setBusqueda("") });
    if (filtroTramo !== "todos")
      chips.push({ key: "tramo", label: `Hasta ${filtroTramo}d`, clear: () => setFiltroTramo("todos") });
    if (fraccionamiento !== "todos")
      chips.push({ key: "fracc", label: fraccionamiento, clear: () => setFraccionamiento("todos") });
    if (filtroCobrador !== "todos")
      chips.push({ key: "cob", label: `Cobrador: ${filtroCobrador}`, clear: () => setFiltroCobrador("todos") });
    if (filtroEstado !== "todos") {
      const lbl = {
        sin_contactar: "Sin contactar",
        contactado: "Contactado",
        con_promesa: "Con promesa",
      };
      chips.push({ key: "est", label: lbl[filtroEstado] || filtroEstado, clear: () => setFiltroEstado("todos") });
    }
    if (filtroDia != null)
      chips.push({ key: "dia", label: `Vence en ${filtroDia}d`, clear: () => setFiltroDia(null) });
    return chips;
  }, [busqueda, filtroTramo, fraccionamiento, filtroCobrador, filtroEstado, filtroDia]);

  const fraccionamientos = useMemo(() => {
    const unique = [...new Set(registros.map((r) => r.fraccionamiento).filter(Boolean))];
    return unique.sort((a, b) => a.localeCompare(b, "es"));
  }, [registros]);

  const cobradores = useMemo(() => {
    return [...new Set(registros.map((r) => r.ultimoCobrador).filter(Boolean))].sort();
  }, [registros]);

  // Map deudaId → promesa pendiente
  const promesasPorDeuda = useMemo(() => {
    const map = new Map();
    for (const p of promesasPendientes) {
      if (p.deudaId) map.set(p.deudaId, p);
    }
    return map;
  }, [promesasPendientes]);

  // Map deudaId → días desde última gestión
  const diasSinGestionPorDeuda = useMemo(() => {
    const ultimoMs = new Map();
    for (const g of gestionesRecientes) {
      if (!g.deudaId) continue;
      const ms = g.creadoEn?.toMillis?.() || toJsMs(g?.creadoEn);
      const prev = ultimoMs.get(g.deudaId) || 0;
      if (ms > prev) ultimoMs.set(g.deudaId, ms);
    }
    const out = new Map();
    const hoyMs = Date.now();
    for (const [id, ms] of ultimoMs.entries()) {
      out.set(id, Math.floor((hoyMs - ms) / 86400000));
    }
    return out;
  }, [gestionesRecientes]);

  // Estado de preparación por registro
  const estadoPreparacionPorDeuda = useCallback(
    (deudaId) => {
      if (promesasPorDeuda.has(deudaId)) return "con_promesa";
      const dias = diasSinGestionPorDeuda.get(deudaId);
      if (dias != null && dias < 7) return "contactado";
      return "sin_contactar";
    },
    [promesasPorDeuda, diasSinGestionPorDeuda],
  );

  // Monto agregado por día (para gráfica calendario)
  const montoPorDia = useMemo(() => {
    const map = new Map();
    for (const r of registros) {
      if (!r.fechaVencimiento) continue;
      // HOTFIX v1.1.5: usar toJsDate (que ya maneja yyyy-mm-dd como LOCAL)
      // en lugar de new Date(string) directo que UTCea y desplaza el día.
      const dt = toJsDate(r.fechaVencimiento);
      if (!dt) continue;
      dt.setHours(0, 0, 0, 0);
      const key = dt.getTime();
      const prev = map.get(key) || { fecha: dt, monto: 0, cuentas: 0, dueDays: r.dueDays };
      prev.monto += Number(r.saldoProximo || 0);
      prev.cuentas += 1;
      map.set(key, prev);
    }
    return [...map.values()]
      .sort((a, b) => a.fecha - b.fecha)
      .map((d) => ({
        diaLabel: `${d.fecha.getDate().toString().padStart(2, "0")}/${(d.fecha.getMonth() + 1).toString().padStart(2, "0")}`,
        dia: d.fecha.getDate(),
        dueDays: d.dueDays,
        monto: Math.round(d.monto),
        cuentas: d.cuentas,
      }));
  }, [registros]);

  // Monto vs snapshot anterior (Δ "este mes vs mes pasado")
  const deltaProyeccion = useMemo(() => {
    if (!snapshots.length) return null;
    // Heurística simple: si tenemos un snapshot >= 25 días atrás, comparamos
    const previo = snapshots.find((s) => {
      const dias = (Date.now() - Number(s.fechaMs || 0)) / 86400000;
      return dias >= 25 && dias <= 40;
    });
    if (!previo) return null;
    // Snapshot solo tiene saldoAtrasado, no saldoProximo. Como proxy:
    // proyecciónActual vs saldoAtrasadoPrevio sería injusto.
    // Mejor: comparar saldoRestante total proyectado vs previo
    // Simplificación: solo mostrar el monto del snapshot previo como referencia.
    return {
      previo,
      diasAtras: Math.round((Date.now() - Number(previo.fechaMs || 0)) / 86400000),
    };
  }, [snapshots]);

  const resumen = useMemo(() => {
  const calcAcumulado = (dias) => {
    const items = registros.filter(
      (r) => r.dueDays != null && r.dueDays >= 1 && r.dueDays <= dias
    );

    return {
      cantidad: items.length,
      monto: items.reduce(
        (acc, item) => acc + Number(item.saldoProximo || 0),
        0
      ),
    };
  };

  return {
    dias7: calcAcumulado(7),
    dias15: calcAcumulado(15),
    dias30: calcAcumulado(30),
    total: registros.length,
    saldoProximoTotal: registros.reduce(
      (acc, item) => acc + Number(item.saldoProximo || 0),
      0
    ),
  };
}, [registros]);

  const registrosFiltrados = useMemo(() => {
    const term = busqueda.trim().toLowerCase();

    const filtered = registros.filter((r) => {
      const byTramo =
        filtroTramo === "todos" ? true : r.dueDays != null && r.dueDays <= Number(filtroTramo);

      const byFracc =
        fraccionamiento === "todos" ? true : r.fraccionamiento === fraccionamiento;

      const byCobrador =
        filtroCobrador === "todos" ? true : r.ultimoCobrador === filtroCobrador;

      const byEstado =
        filtroEstado === "todos" ? true : estadoPreparacionPorDeuda(r.id) === filtroEstado;

      const byDia = filtroDia == null ? true : r.dueDays === filtroDia;

      const bySearch = !term || agendaCobranza.getTextoBusquedaAgenda(r).includes(term);

      return byTramo && byFracc && byCobrador && byEstado && byDia && bySearch;
    });

    filtered.sort((a, b) => {
      if (orden === "cliente") {
        return String(a.cliente || "").localeCompare(String(b.cliente || ""), "es");
      }

      if (orden === "monto") {
        return Number(b.mensualidad || 0) - Number(a.mensualidad || 0);
      }

      if (orden === "saldo") {
        return Number(b.saldoRestante || 0) - Number(a.saldoRestante || 0);
      }

      if (orden === "saldoProximo") {
        return Number(b.saldoProximo || 0) - Number(a.saldoProximo || 0);
      }

      if (orden === "dia") {
        return Number(a.diaDelMes || 0) - Number(b.diaDelMes || 0);
      }

      return (a.nextDueDate?.seconds || 0) - (b.nextDueDate?.seconds || 0);
    });

    return filtered;
  }, [
    registros,
    busqueda,
    filtroTramo,
    fraccionamiento,
    filtroCobrador,
    filtroEstado,
    filtroDia,
    estadoPreparacionPorDeuda,
    orden,
  ]);

  const exportarAgendaExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Agenda previsoria", {
        views: [{ state: "frozen", ySplit: 4 }],
      });

      const hoy = new Date();
      const fechaTexto = hoy.toLocaleDateString("es-MX");

      worksheet.mergeCells("A1:J1");
      worksheet.getCell("A1").value = "AGENDA PREVISORIA DE COBRANZA";
      worksheet.getCell("A1").font = {
        bold: true,
        size: 16,
        color: { argb: "FFFFFFFF" },
      };
      worksheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
      worksheet.getCell("A1").fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "1F4E78" },
      };

      worksheet.mergeCells("A2:J2");
      worksheet.getCell("A2").value = `Fecha de exportación: ${fechaTexto}`;
      worksheet.getCell("A2").font = { italic: true, color: { argb: "FF444444" } };

      worksheet.mergeCells("A3:J3");
      worksheet.getCell("A3").value =
        `Filtro: ${filtroTramo === "todos" ? "Todos" : `Hasta ${filtroTramo} días`} | Registros: ${registrosFiltrados.length}`;

      const headerRow = worksheet.getRow(4);
      headerRow.values = [
        "Día del mes",
        "Cliente",
        "Responsable",
        "Mensualidad",
        "Saldo próximo",
        "Saldo restante",
        "Fraccionamiento",
        "Referencia",
        "Dirección",
        "Teléfono",
      ];
      headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "4472C4" },
      };

      registrosFiltrados.forEach((item) => {
        const row = worksheet.addRow([
          item.diaDelMes ?? "",
          item.cliente || "",
          item.responsableNombre || "",
          Number(item.mensualidad || 0),
          Number(item.saldoProximo || 0),
          Number(item.saldoRestante || 0),
          item.fraccionamiento || "",
          item.referencia || "",
          item.direccion || "",
          item.telefono || "",
        ]);

        row.getCell(4).numFmt = "$#,##0.00;[Red]-$#,##0.00";
        row.getCell(5).numFmt = "$#,##0.00;[Red]-$#,##0.00";
        row.getCell(6).numFmt = "$#,##0.00;[Red]-$#,##0.00";

        let fillColor = "DDEBF7";
        if ((item.dueDays ?? 999) <= 7) fillColor = "FFF2CC";
        else if ((item.dueDays ?? 999) <= 15) fillColor = "FCE4D6";

        row.getCell(1).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: fillColor },
        };
        row.getCell(1).font = { bold: true };
        row.getCell(1).alignment = { horizontal: "center" };
      });

      worksheet.columns = [
        { width: 12 },
        { width: 28 },
        { width: 24 },
        { width: 14 },
        { width: 16 },
        { width: 16 },
        { width: 22 },
        { width: 18 },
        { width: 40 },
        { width: 18 },
      ];

      worksheet.autoFilter = { from: "A4", to: "J4" };

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `Agenda_Previsoria_${fechaTexto.replaceAll("/", "-")}.xlsx`
      );
    } catch (error) {
      console.error("Error exportando Excel:", error);
      notify.error("No se pudo exportar el archivo Excel.");
    }
  };

  return (
    <div className="agenda-previsoria-page">
      <div className="agenda-previsoria-header">
        <div>
          <h2>Agenda Previsoria</h2>
          <p>Próximos vencimientos con saldo recalculado en tiempo real.</p>
        </div>

        <div className="agenda-previsoria-top-actions">
          <button
            className="ap-btn ap-btn-light"
            onClick={() => setVistaCompacta((v) => !v)}
            title="Alternar vista compacta/extendida"
          >
            {vistaCompacta ? "Vista extendida" : "Vista compacta"}
          </button>
          <button
            className="ap-btn ap-btn-light"
            onClick={() => setAgrupar((v) => !v)}
            title="Agrupar por semana"
          >
            {agrupar ? "Lista plana" : "Agrupar semana"}
          </button>
          <button className="ap-btn ap-btn-light" onClick={() => navigate("/app/agenda")}>
            Agenda del día
          </button>
          <button className="ap-btn ap-btn-light" onClick={() => navigate("/app/agenda/vencida")}>
            Agenda vencida
          </button>
          <button className="ap-btn ap-btn-dark" onClick={exportarAgendaExcel}>
            Exportar Excel
          </button>
        </div>
      </div>

      {/* ═════ HERO ═════ */}
      <div className="ap-hero">
        <div className="ap-hero-main">
          <span className="ap-hero-label">Por cobrar próximos 30 días</span>
          <strong className="ap-hero-value">
            {agendaCobranza.formatMoney(resumen.saldoProximoTotal)}
          </strong>
          <span className="ap-hero-sub">
            {resumen.total} cuentas con vencimiento programado
          </span>
        </div>
        <div className="ap-hero-mini">
          <div className="ap-hero-mini-item">
            <span>Próx. 7 días</span>
            <strong>{agendaCobranza.formatMoney(resumen.dias7.monto)}</strong>
            <small>{resumen.dias7.cantidad} cuentas</small>
          </div>
          <div className="ap-hero-mini-item">
            <span>Próx. 15 días</span>
            <strong>{agendaCobranza.formatMoney(resumen.dias15.monto)}</strong>
            <small>{resumen.dias15.cantidad} cuentas</small>
          </div>
          <div className="ap-hero-mini-item">
            <span>Próx. 30 días</span>
            <strong>{agendaCobranza.formatMoney(resumen.dias30.monto)}</strong>
            <small>{resumen.dias30.cantidad} cuentas</small>
          </div>
          {deltaProyeccion?.previo && (
            <div className="ap-hero-mini-item ap-hero-mini-prev">
              <span>Snapshot hace {deltaProyeccion.diasAtras}d</span>
              <strong>
                {agendaCobranza.formatMoney(deltaProyeccion.previo?.totales?.totalSaldoAtrasado || 0)}
              </strong>
              <small>Referencia histórica</small>
            </div>
          )}
        </div>
      </div>

      {/* ═════ CALENDARIO VISUAL ═════ */}
      {montoPorDia.length > 0 && (
        <div className="ap-calendario">
          <div className="ap-calendario-head">
            <h3>Monto esperado por día</h3>
            <small>Click en una barra para filtrar</small>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart
              data={montoPorDia}
              onClick={(e) => {
                if (e?.activePayload?.[0]?.payload?.dueDays != null) {
                  setFiltroDia(e.activePayload[0].payload.dueDays);
                }
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="diaLabel" stroke="#64748b" fontSize={11} />
              <YAxis
                stroke="#64748b"
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                fontSize={11}
              />
              <RTooltip
                formatter={(value, name) =>
                  name === "monto" ? agendaCobranza.formatMoney(value) : value
                }
              />
              <Bar
                dataKey="monto"
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
                cursor="pointer"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="agenda-previsoria-controls">
        <div className="ap-tabs">
          <button className={filtroTramo === "todos" ? "active" : ""} onClick={() => setFiltroTramo("todos")}>
            Todos
          </button>
          <button className={filtroTramo === "7" ? "active" : ""} onClick={() => setFiltroTramo("7")}>
            7 días
          </button>
          <button className={filtroTramo === "15" ? "active" : ""} onClick={() => setFiltroTramo("15")}>
            15 días
          </button>
          <button className={filtroTramo === "30" ? "active" : ""} onClick={() => setFiltroTramo("30")}>
            30 días
          </button>
        </div>

        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar cliente, responsable, teléfono, referencia..."
        />

        <select value={fraccionamiento} onChange={(e) => setFraccionamiento(e.target.value)}>
          <option value="todos">Todos los fraccionamientos</option>
          {fraccionamientos.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>

        <select value={filtroCobrador} onChange={(e) => setFiltroCobrador(e.target.value)}>
          <option value="todos">Todos los cobradores</option>
          {cobradores.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
          <option value="todos">Cualquier estado</option>
          <option value="sin_contactar">🆕 Sin contactar</option>
          <option value="contactado">📞 Contactado</option>
          <option value="con_promesa">📅 Con promesa</option>
        </select>

        <select value={orden} onChange={(e) => setOrden(e.target.value)}>
          <option value="vencimiento">Ordenar por vencimiento</option>
          <option value="dia">Ordenar por día del mes</option>
          <option value="cliente">Ordenar por cliente</option>
          <option value="monto">Ordenar por mensualidad</option>
          <option value="saldo">Ordenar por saldo restante</option>
          <option value="saldoProximo">Ordenar por saldo próximo</option>
        </select>
      </div>

      {/* ═════ CHIPS DE FILTROS ACTIVOS ═════ */}
      {filtrosActivos.length > 0 && (
        <div className="ap-chips-row">
          <span className="ap-chips-label">Filtros activos:</span>
          {filtrosActivos.map((chip) => (
            <button key={chip.key} className="ap-chip" onClick={chip.clear}>
              {chip.label} <span className="ap-chip-x">×</span>
            </button>
          ))}
          <button className="ap-chip-clear" onClick={limpiarFiltros}>
            Limpiar todo
          </button>
          <span className="ap-chips-count">
            Mostrando {registrosFiltrados.length} de {registros.length}
          </span>
        </div>
      )}

      {/* ═════ BARRA DE ACCIONES EN LOTE ═════ */}
      {seleccionados.size > 0 && (
        <div className="ap-bulk-bar">
          <span className="ap-bulk-count">
            <strong>{seleccionados.size}</strong> cuentas seleccionadas
          </span>
          <button
            className="ap-btn ap-btn-dark"
            onClick={() =>
              enviarRecordatoriosLote(
                registrosFiltrados.filter((it) => seleccionados.has(it.id)),
              )
            }
          >
            💬 Enviar recordatorios WhatsApp ({seleccionados.size})
          </button>
          <button className="ap-btn ap-btn-light" onClick={limpiarSeleccion}>
            Limpiar selección
          </button>
        </div>
      )}

      <div className={`agenda-previsoria-table-wrap ${vistaCompacta ? "is-compacta" : ""}`}>
        <table className="agenda-previsoria-table">
          <thead>
            <tr>
              <th className="ap-th-check">
                <input
                  type="checkbox"
                  checked={
                    registrosFiltrados.length > 0 &&
                    registrosFiltrados.every((it) => seleccionados.has(it.id))
                  }
                  onChange={() => togglearTodos(registrosFiltrados)}
                />
              </th>
              <th>Día</th>
              <th>Cliente</th>
              {!vistaCompacta && <th>Responsable</th>}
              <th>Mensualidad</th>
              <th>Saldo próximo</th>
              {!vistaCompacta && <th>Saldo restante</th>}
              <th>Vence</th>
              {!vistaCompacta && <th>Fraccionamiento</th>}
              {!vistaCompacta && <th>Referencia</th>}
              {!vistaCompacta && <th>Dirección</th>}
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={vistaCompacta ? 8 : 12} className="ap-empty">
                  Cargando agenda previsoria...
                </td>
              </tr>
            ) : registrosFiltrados.length === 0 ? (
              <tr>
                <td colSpan={vistaCompacta ? 8 : 12} className="ap-empty">
                  No hay registros para los filtros actuales.
                </td>
              </tr>
            ) : (
              registrosFiltrados.map((item) => {
                const estado = estadoPreparacionPorDeuda(item.id);
                const promesa = promesasPorDeuda.get(item.id);
                const diasSinG = diasSinGestionPorDeuda.get(item.id);
                return (
                <tr key={item.id} className={`ap-row-${estado}`}>
                  <td className="ap-td-check">
                    <input
                      type="checkbox"
                      checked={seleccionados.has(item.id)}
                      onChange={() => toggleSeleccionado(item.id)}
                    />
                  </td>
                  <td>
                    <span className={`ap-day ap-day-${item.tramo}`}>
                      {String(item.diaDelMes || "—").padStart(2, "0")}
                    </span>
                  </td>
                  <td className="ap-cliente-cell">
                    <strong>{item.cliente}</strong>
                    <div className="ap-cliente-meta">
                      <span className="ap-mini-text">{item.telefono}</span>
                      {item.telefono && item.telefono !== "—" && (
                        <span className="ap-contact-actions">
                          <button
                            type="button"
                            className="ap-icon-btn"
                            title="Copiar teléfono"
                            onClick={() => copiarTelefono(item.telefono)}
                          >
                            📋
                          </button>
                          <button
                            type="button"
                            className="ap-icon-btn ap-icon-btn-wa"
                            title="Enviar recordatorio WhatsApp"
                            onClick={() => abrirWhatsAppRecordatorio(item)}
                          >
                            💬
                          </button>
                        </span>
                      )}
                    </div>
                    <div className="ap-badges-row">
                      {estado === "con_promesa" && (
                        <span
                          className="ap-badge ap-badge-promesa"
                          title={`Promesa para ${promesa?.fechaPromesa}`}
                        >
                          📅 Con promesa
                        </span>
                      )}
                      {estado === "contactado" && (
                        <span
                          className="ap-badge ap-badge-contactado"
                          title={`Última gestión hace ${diasSinG}d`}
                        >
                          📞 Contactado
                        </span>
                      )}
                      {estado === "sin_contactar" && (
                        <span className="ap-badge ap-badge-sin">🆕 Sin contactar</span>
                      )}
                      {vistaCompacta && item.fraccionamiento && (
                        <span className="ap-badge ap-badge-fracc">{item.fraccionamiento}</span>
                      )}
                    </div>
                  </td>
                  {!vistaCompacta && <td>{item.responsableNombre || "—"}</td>}
                  <td>{agendaCobranza.formatMoney(item.mensualidad)}</td>
                  <td>{agendaCobranza.formatMoney(item.saldoProximo)}</td>
                  {!vistaCompacta && <td>{agendaCobranza.formatMoney(item.saldoRestante)}</td>}
                  <td>{agendaCobranza.formatDate(item.nextDueDate)}</td>
                  {!vistaCompacta && <td>{item.fraccionamiento || "—"}</td>}
                  {!vistaCompacta && <td>{item.referencia || "—"}</td>}
                  {!vistaCompacta && <td className="ap-address">{item.direccion || "—"}</td>}
                  <td className="ap-actions-cell">
                    <button className="ap-mini ap-view" onClick={() => handleVerDetalle(item)}>
                      Ver detalle
                    </button>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AgendaPrevisoria;